import { useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Flex, Text, TextField } from '@radix-ui/themes'
import cn from 'classnames'
import { WizardSubmitButton } from 'features/auth/components/wizard-submit-button'
import { useCheckNames } from 'features/auth/hooks/use-check-name'
import { DataTestIDs } from 'shared/constants'
import { MAX_USER_NAME_LENGHT } from 'shared/constants/restrictive-units'
import { useValidateUserName } from 'shared/hooks/use-validate-user-name'
import { DotTitle, XyroDialog } from 'shared/ui'
import { getPrivySessionBridge } from '../session-bridge'
import { isPrivyExchangingVar, isPrivyUsernameDialogOpenVar } from '../store'

const MIN_USER_NAME_LENGTH = 3

/**
 * Nickname step for first-time Privy users: `signInPrivy` answers
 * USERNAME_REQUIRED, the user picks a name and the exchange is retried.
 * Closing the dialog drops the Privy session, so nobody stays half-signed-in.
 */
export const PrivyUsernameDialog = () => {
  const isOpen = useReactiveVar(isPrivyUsernameDialogOpenVar)
  const isExchanging = useReactiveVar(isPrivyExchangingVar)
  const { name, handleNameChange, isNameInvalid } = useValidateUserName()
  const { checkNames, loading } = useCheckNames()

  const handleSubmit = useCallback(async () => {
    const isNameAvailable = await checkNames(name)
    if (!isNameAvailable) return

    await getPrivySessionBridge()?.submitUsername(name)
  }, [checkNames, name])

  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    if (nextIsOpen) return

    isPrivyUsernameDialogOpenVar(false)
    void getPrivySessionBridge()?.logout()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    e.key === 'Enter' && name.length >= MIN_USER_NAME_LENGTH && handleSubmit()

  return (
    <XyroDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Flex
        direction='column'
        justify='center'
        align='center'
        width='100%'
        gap='2'
        p='6'
      >
        <Flex
          direction='column'
          gap='1'
          my='3'
          width='100%'
        >
          <Flex
            align='center'
            justify='between'
            width='100%'
          >
            <DotTitle>NAME</DotTitle>

            <Text
              size='2'
              weight='medium'
              className={cn('color-white', { ['color-pink']: isNameInvalid })}
            >
              {name.length}/{MAX_USER_NAME_LENGHT}
            </Text>
          </Flex>

          <TextField.Root
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            value={name}
            radius='large'
            type='text'
            size='3'
            data-testid={DataTestIDs.privyUsernameInput}
          />
        </Flex>

        <Text
          size='2'
          className='color-white'
        >
          Your nickname will be visible to other users. You can change it later
          in the settings.
        </Text>

        <WizardSubmitButton
          type='button'
          onClick={handleSubmit}
          isLoading={loading || isExchanging}
          disabled={name.length < MIN_USER_NAME_LENGTH}
          data-testid={DataTestIDs.buttonPrivyUsernameSubmit}
        >
          Continue
        </WizardSubmitButton>
      </Flex>
    </XyroDialog>
  )
}
